/** Finder-style folder card — navigate, rename, recolor, and delete a personal lesson folder. */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, MoreHorizontal, Pencil, Palette, Trash2 } from 'lucide-react';
import { Button as AriaButton } from 'react-aria-components';
import { toast } from 'sonner';
import {
  Surface,
  Dialog,
  TextField,
  ColorPalettePicker,
  MenuTrigger,
  Menu,
  MenuItem,
  MenuSeparator,
  Button,
} from '@/interfaces/learner/ui';
import { useRenameFolder, useDeleteFolder } from '@/shared/api/hooks';
import { slugify } from '../utils';
import type { PersonalLessonFolder } from '@formency/shared';

interface FolderCardProps {
  folder: PersonalLessonFolder;
}

export function FolderCard({ folder }: FolderCardProps) {
  const navigate = useNavigate();
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();

  const [showRename, setShowRename] = useState(false);
  const [showColor, setShowColor] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const [colorValue, setColorValue] = useState(folder.color ?? undefined);

  const color = folder.color ?? 'var(--color-text-muted)';

  const handleNavigate = () => {
    navigate(`/lessons/${slugify(folder.name)}`);
  };

  const handleRenameSubmit = () => {
    const name = renameValue.trim();
    if (!name || name === folder.name) {
      setShowRename(false);
      return;
    }
    renameFolder.mutate(
      { folderId: folder.id, name, color: folder.color },
      {
        onSuccess: () => setShowRename(false),
        onError: (err) => toast.error(err.message || 'Failed to rename folder'),
      }
    );
  };

  const handleColorSubmit = () => {
    renameFolder.mutate(
      { folderId: folder.id, name: folder.name, color: colorValue ?? null },
      {
        onSuccess: () => setShowColor(false),
        onError: (err) => toast.error(err.message || 'Failed to update color'),
      }
    );
  };

  const handleDelete = () => {
    deleteFolder.mutate(folder.id, {
      onSuccess: () => setShowDelete(false),
      onError: (err) => toast.error(err.message || 'Failed to delete folder'),
    });
  };

  return (
    <>
      <Surface
        interactive
        onClick={handleNavigate}
        style={{ position: 'relative', minHeight: 110 }}
      >
        {/* Three-dot menu — top-right */}
        <div
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuTrigger>
            <AriaButton
              aria-label="Folder options"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 'var(--control-radius-sm)',
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-muted)',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              className="hover:bg-[var(--color-surface-muted)]"
            >
              <MoreHorizontal size={15} strokeWidth={2} />
            </AriaButton>
            <Menu>
              <MenuItem
                icon={<Pencil size={14} strokeWidth={1.75} />}
                onAction={() => {
                  setRenameValue(folder.name);
                  setShowRename(true);
                }}
              >
                Rename
              </MenuItem>
              <MenuItem
                icon={<Palette size={14} strokeWidth={1.75} />}
                onAction={() => {
                  setColorValue(folder.color ?? undefined);
                  setShowColor(true);
                }}
              >
                Change color
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                variant="danger"
                icon={<Trash2 size={14} strokeWidth={1.75} />}
                onAction={() => setShowDelete(true)}
              >
                Delete
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </div>

        {/* Card body */}
        <div style={{ padding: '20px 16px 16px' }}>
          {/* Folder icon */}
          <FolderOpen size={28} strokeWidth={1.5} style={{ color, marginBottom: 10 }} />

          {/* Folder name */}
          <p
            className="text-list-title"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              paddingRight: 24,
            }}
          >
            {folder.name}
          </p>

          {/* Lesson count */}
          <p
            className="text-list-meta-muted"
            style={{
              marginTop: 3,
            }}
          >
            {folder.lessonCount} {folder.lessonCount === 1 ? 'lesson' : 'lessons'}
          </p>
        </div>
      </Surface>

      {/* Rename dialog */}
      <Dialog
        isOpen={showRename}
        onClose={() => setShowRename(false)}
        title="Rename folder"
        size="sm"
      >
        <TextField
          label="Folder name"
          value={renameValue}
          onChange={setRenameValue}
          size="md"
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowRename(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={renameFolder.isPending}
            onPress={handleRenameSubmit}
          >
            Rename
          </Button>
        </div>
      </Dialog>

      {/* Change color dialog */}
      <Dialog
        isOpen={showColor}
        onClose={() => setShowColor(false)}
        title="Change folder color"
        size="sm"
      >
        <ColorPalettePicker value={colorValue} onChange={setColorValue} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowColor(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={renameFolder.isPending}
            onPress={handleColorSubmit}
          >
            Save
          </Button>
        </div>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        title="Delete folder?"
        size="sm"
      >
        <p
          className="text-body"
          style={{
            margin: 0,
          }}
        >
          Lessons in this folder will be moved to Unfiled.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onPress={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            isDisabled={deleteFolder.isPending}
            onPress={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </>
  );
}
